import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiX, FiSmartphone, FiMonitor, FiCheckCircle } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './AppInstallsModal.css';

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const platformIcon = (platform) => {
  const p = String(platform).toLowerCase();
  if (p === 'web') return <FiMonitor />;
  return <FiSmartphone />;
};

const AppInstallsModal = ({ open, onClose, playStoreLabel }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const fetchDevices = async () => {
      setLoading(true);
      setError('');
      try {
        const token = getAuthToken();
        const res = await axios.get(`${API_URL}/admin/analytics/app-installs/devices`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.error || 'Failed to load installed devices');
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
  }, [open]);

  if (!open) return null;

  const mobileDevices = (data?.devices || []).filter((d) => d.install_type === 'mobile');
  const webDevices = (data?.devices || []).filter((d) => d.install_type === 'web');

  return (
    <div className="app-installs-modal__backdrop" onClick={onClose}>
      <div className="app-installs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="app-installs-modal__head">
          <div>
            <h2>Installed Devices</h2>
            <p>
              Exact mobile installs: <strong>{data?.exact_install_count ?? 0}</strong>
              {playStoreLabel && playStoreLabel !== '—' && (
                <span className="app-installs-modal__estimate"> · Play Store estimate: {playStoreLabel}</span>
              )}
            </p>
          </div>
          <button type="button" className="app-installs-modal__close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="app-installs-modal__body">
          {loading && <p className="app-installs-modal__status">Loading devices...</p>}
          {error && <p className="app-installs-modal__error">{error}</p>}

          {!loading && !error && (
            <>
              <section className="app-installs-modal__section">
                <h3>Mobile App ({mobileDevices.length})</h3>
                {mobileDevices.length === 0 ? (
                  <p className="app-installs-modal__empty">
                    No mobile app installations tracked yet. Devices are registered when users open the mobile app.
                    The Play Store estimate ({playStoreLabel || '—'}) includes all store installs; the exact count above reflects only app-registered devices.
                  </p>
                ) : (
                  <div className="app-installs-modal__list">
                    {mobileDevices.map((device) => (
                      <article key={device.id} className="app-installs-modal__card">
                        <div className="app-installs-modal__card-icon">{platformIcon(device.platform)}</div>
                        <div className="app-installs-modal__card-main">
                          <div className="app-installs-modal__card-top">
                            <strong>{device.device_name}</strong>
                            <span className="app-installs-modal__badge app-installs-modal__badge--installed">
                              <FiCheckCircle /> Installed
                            </span>
                          </div>
                          <p className="app-installs-modal__meta">
                            {device.platform} · {device.owner_name}
                            {device.owner_email ? ` · ${device.owner_email}` : ''}
                          </p>
                          <p className="app-installs-modal__meta app-installs-modal__meta--muted">
                            Role: {device.owner_role} · Last active: {formatDate(device.last_active_at)}
                          </p>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              {webDevices.length > 0 && (
                <section className="app-installs-modal__section">
                  <h3>Web App ({webDevices.length})</h3>
                  <div className="app-installs-modal__list">
                    {webDevices.map((device) => (
                      <article key={device.id} className="app-installs-modal__card">
                        <div className="app-installs-modal__card-icon">{platformIcon(device.platform)}</div>
                        <div className="app-installs-modal__card-main">
                          <div className="app-installs-modal__card-top">
                            <strong>{device.device_name}</strong>
                            <span className="app-installs-modal__badge app-installs-modal__badge--installed">
                              <FiCheckCircle /> Active
                            </span>
                          </div>
                          <p className="app-installs-modal__meta">
                            {device.owner_name}
                            {device.owner_email ? ` · ${device.owner_email}` : ''}
                          </p>
                          <p className="app-installs-modal__meta app-installs-modal__meta--muted">
                            Role: {device.owner_role} · Last active: {formatDate(device.last_active_at)}
                          </p>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppInstallsModal;
